/* eslint-disable object-property-newline */
/* eslint-disable max-len */
import React from 'react';
import classnames from 'classnames';
import { ValueCache } from '@leverege/value-cache';
import Row from "./Row.js";
import S from './LinearView.css';
import CursorContext from "./CursorContext.js";
import CursorEvent from "./CursorEvent.js";
/**
 * React class that renders the main veiw of the ghant chart
 * Props:
 * @property {function|string} rowClass return className for rowData
 * @property {function|string} blockClass return className for block
 * @property {function} rowAccess return array of rows from data
 * @property {function} onClick callback when a block is clicked
 * @property {function} onClick callback when a block is clicked
 * @property {function} startAccess returns start value from a block
 * @property {function} stopAccess returns stop value from a block
 * @property {function} blockStylizer function( item, project, startValue stopValue, x1, x2 ) and returns { className, style, children } options for the block. Good for dynamic styling
 * @property {function} setScroll function to be used to set the scrollTop and scrollLeft value of the element
 * @property {Ref} scrollRef If supplied, the current ref's scrollRefKey function will be invoked with 
 * a { top, left, headerHeight, header, content } object to allow the scrollRef to update its scroll position.
 * @property {Ref} scrollRefKey The name of the method to invoke on scroll (default: 'onContentScroll')
 * @property {Ref} onScroll If supplied,the function will be invoked with a { top, left, headerHeight, header, content } object to 
 * allow the tracking of the linear view's scroll position.
 **/
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
class LinearView extends React.Component {
  constructor(props) {
    super(props);
    this.headerRef = /*#__PURE__*/React.createRef();
    this.contentRef = /*#__PURE__*/React.createRef();
    this.contentCache = new ValueCache();
    this.state = {
      cursorEvent: null
    };
  }
  componentDidMount() {
    this.onContentScroll();
  }
  onRollover = event => {
    const {
      onRollover,
      data
    } = this.props;
    if (onRollover) {
      onRollover({
        ...event,
        linearView: this,
        allData: data
      });
    }
  };
  onClick = event => {
    const {
      onClick,
      data
    } = this.props;
    if (onClick) {
      onClick({
        ...event,
        linearView: this,
        allData: data
      });
    }
  };
  toRow(rowData, index) {
    const {
      rowClass,
      onClick,
      onRollover,
      startAccess,
      stopAccess,
      children,
      rowComponent,
      ...rest
    } = this.props;
    const clzz = typeof rowClass === 'string' ? rowClass : typeof rowClass === 'function' ? rowClass(rowData, index) : null;
    return /*#__PURE__*/_jsx(Row, {
      rowComponent: rowComponent || 'div',
      className: clzz,
      rowData: rowData,
      onClick: this.onClick,
      startAccess: startAccess,
      stopAccess: stopAccess,
      onRollover: this.onRollover,
      ...rest
    }, `row-${index}`);
  }
  setScroll(top, left) {
    if (top != null) {
      this.contentRef.current.scrollTop = top;
    }
    if (left != null) {
      this.contentRef.current.scrollLeft = left;
    }
  }
  onContentScroll = evt => {
    if (this.lock === true) {
      return;
    }
    this.lock = true;
    const left = this.contentRef.current.scrollLeft;
    const top = this.contentRef.current.scrollTop;
    const {
      onScroll,
      scrollRef,
      scrollRefKey = 'onContentScroll'
    } = this.props;
    window.requestAnimationFrame(() => {
      let headerHeight = 0;
      if (this.headerRef.current) {
        this.headerRef.current.scrollLeft = left;
        headerHeight = this.headerRef.current.clientHeight;
      }
      const sEvt = {
        top,
        left,
        headerHeight,
        content: this.contentRef.current,
        header: this.headerRef.current
      };
      if (onScroll && typeof onScroll === 'function') {
        onScroll(sEvt);
      }
      const rowHdr = scrollRef && scrollRef.current;
      if (rowHdr && typeof rowHdr[scrollRefKey] === 'function') {
        rowHdr[scrollRefKey](sEvt);
      }
      this.lock = false;
    });
    // if ( onScroll && evt ) {
    //   onScroll( evt )
    // }
  };
  onMouseMove = evt => {
    const {
      projection,
      onTrackMouse,
      data,
      startAccess,
      stopAccess,
      blockAccess
    } = this.props;
    const {
      clientX,
      clientY
    } = evt;
    const bnd = this.contentRef.current.getBoundingClientRect();
    const x = clientX - bnd.left + this.contentRef.current.scrollLeft;
    const y = clientY - bnd.top + this.contentRef.current.scrollTop;
    const t = projection.toValue(x);
    const cursorEvent = new CursorEvent({
      pixelX: x,
      pixelY: y,
      value: t,
      rows: data,
      projection,
      linearView: this,
      event: evt,
      startAccess,
      stopAccess,
      blockAccess
    });
    this.setState({
      cursorEvent
    });
    if (typeof onTrackMouse === 'function') {
      onTrackMouse(cursorEvent);
    }
  };
  onMouseExit = () => {
    this.setState({
      cursorEvent: null
    });
  };
  render() {
    const {
      cursorEvent
    } = this.state;
    const {
      className,
      contentClassName,
      rowAccess,
      children,
      data = [],
      style,
      extraContent,
      extraRootContent,
      onTrackMouse,
      rootProps,
      headerProps,
      contentProps
    } = this.props;
    const rows = rowAccess == null ? data : rowAccess(data);
    const content = this.contentCache.calc(() => {
      return /*#__PURE__*/_jsxs("div", {
        className: classnames(S.linearView, className),
        style: style,
        ...rootProps,
        onMouseLeave: onTrackMouse ? this.onMouseExit : null,
        onMouseMove: onTrackMouse ? this.onMouseMove : null,
        children: [extraRootContent, /*#__PURE__*/_jsx("div", {
          className: S.header,
          ref: this.headerRef,
          ...headerProps,
          children: children
        }), /*#__PURE__*/_jsxs("div", {
          className: classnames(S.content, contentClassName),
          ref: this.contentRef,
          onScroll: this.onContentScroll,
          ...contentProps,
          children: [rows.map((rowData, index) => this.toRow(rowData, index)), extraContent]
        })]
      });
    }, className, contentClassName, rowAccess, children, data, style, extraContent, rootProps, headerProps, contentProps);
    if (onTrackMouse) {
      return /*#__PURE__*/_jsx(CursorContext.Provider, {
        value: cursorEvent,
        children: content
      });
    }
    return content;
  }
}
export default LinearView;